import { forwardRef, type HTMLAttributes } from "react";
import { cn } from "../../lib/utils";

// Mirrored verbatim from NOX dashboard (src/components/ui/table.tsx).
const Table = forwardRef<HTMLTableElement, HTMLAttributes<HTMLTableElement>>(
  ({ className, ...props }, ref) => (
    <div className="relative w-full overflow-auto">
      <table
        ref={ref}
        className={cn("w-full caption-bottom text-sm", className)}
        style={{ borderCollapse: "separate", borderSpacing: "0 6px" }}
        {...props}
      />
    </div>
  )
);
Table.displayName = "Table";

const TableHeader = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <thead ref={ref} className={cn("[&_tr]:border-0", className)} {...props} />
  )
);
TableHeader.displayName = "TableHeader";

const TableBody = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <tbody ref={ref} className={cn("", className)} {...props} />
  )
);
TableBody.displayName = "TableBody";

const TableRow = forwardRef<HTMLTableRowElement, HTMLAttributes<HTMLTableRowElement>>(
  ({ className, style, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn("group transition-colors", className)}
      style={{
        backgroundColor: "#1e1e1e",
        border: "1px solid transparent",
        ...style,
      }}
      onMouseEnter={(e) => {
        if (!e.currentTarget.closest("thead"))
          (e.currentTarget as HTMLElement).style.backgroundColor = "#252525";
      }}
      onMouseLeave={(e) => {
        if (!e.currentTarget.closest("thead"))
          (e.currentTarget as HTMLElement).style.backgroundColor = "#1e1e1e";
      }}
      {...props}
    />
  )
);
TableRow.displayName = "TableRow";

const TableHead = forwardRef<HTMLTableCellElement, HTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <th
      ref={ref}
      className={cn(
        "px-4 py-3 text-left align-middle text-[10px] uppercase tracking-widest font-normal first:pl-4",
        className
      )}
      style={{ color: "#adaaaa" }}
      {...props}
    />
  )
);
TableHead.displayName = "TableHead";

const TableCell = forwardRef<HTMLTableCellElement, HTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <td
      ref={ref}
      className={cn("px-4 py-3 align-middle first:rounded-l-xl last:rounded-r-xl", className)}
      {...props}
    />
  )
);
TableCell.displayName = "TableCell";

export { Table, TableHeader, TableBody, TableRow, TableHead, TableCell };
